


















































































































































import { EstimatedShippingMethod, useSveaOrder, SveaConfig } from '@wemade-vsf/payment-svea';
import { PropType,defineComponent, computed, ref, useRoute, useRouter, onMounted, useContext, watchEffect } from '@nuxtjs/composition-api';
import { useCart, useUser } from '@wemade-vsf/customer';
import { useCartView, useMakeOrder } from '@wemade-vsf/checkout';
import { SfButton, SfLoader, SfHeading, SfAlert } from '@storefront-ui/vue';
import CouponCode from 'components/checkout/Cart/CouponCode.vue';

export default defineComponent({
  name: 'CartTotals',
  props: {
    sveaConfig: {
      type: Object as PropType<SveaConfig | null | undefined>,
      default: null
    }
  },
  components: {
    SfButton,
    SfLoader,
    SfHeading,
    SfAlert,
    CouponCode
  },
  setup(props) {

    const { user } = useUser();
    const productTaxDiff = computed(() => {
      const items = cart.value?.items || [];
      if (!items.length) return 0;
      return items.reduce((acc, item) => {
        const incl = item.prices?.row_total_including_tax?.value || 0;
        const excl = item.prices?.row_total?.value || 0;
        return acc + (incl - excl);
      }, 0);
    });

    const shippingInclTaxValue = computed(() => {
      return (totalsVat.value || 0) - (productTaxDiff.value || 0);
    });
    const shippingTaxTotal = computed(() => {
      return (shippingInclTaxValue.value + shippingTotal.value) || 0;
    });
    const { app } = useContext();
    const userTaxvat = computed(() => {
      return user.value?.taxvat || '';
    });
    const isAddressFormOpen = ref(false);

    onMounted(() => {
      isAddressFormOpen.value = !!document.querySelector('.customer-address form.form');
    });

    if (process.client) {
      const observer = new MutationObserver(() => {
        isAddressFormOpen.value = !!document.querySelector('.customer-address form.form');
      });
      observer.observe(document.body, { childList: true, subtree: true });
    }
    const route = useRoute();
    const router = useRouter(); 
    const isSveaCheckout = computed(() => {
      return route.value.path.replace(/\/+$/, '') === '/sveacheckout';
    });
    
    const showCoupon = ref(false);
    const { cart, setCart, load: loadCart } = useCart();
    const cartView = useCartView();
    const { make, loading: isPlacingOrder } = useMakeOrder();
    const order = ref(null);
    const creditCheckApproved = computed(() => {
      if (process.client) {
        return JSON.parse(localStorage.getItem('credit_check_approved') || 'false') === true;
      }
      return false;
    });
    const { getEstimatedShippingMethods } = useSveaOrder();

    const getShippingAmount = (method: EstimatedShippingMethod) => {
      if (!method || !method.price_excl_tax) return null
      return `${app.$fc(method.price_incl_tax)} <small>(${app.$fc(method.price_excl_tax)} ${app.i18n.t('excl. VAT')})</small>`
    }

    const canCheckout = computed(() => {
      return (
        cart.value?.billing_address?.street &&
        cart.value?.shipping_addresses?.[0]?.street &&
        cart.value?.selected_payment_method?.code &&
        cart.value?.shipping_addresses?.[0]?.selected_shipping_method?.method_code
      );
    });

    const hasValidPhones = computed(() => {
      const billingPhone = cart.value?.billing_address?.telephone;
      const shippingPhone = cart.value?.shipping_addresses?.[0]?.telephone;
      return billingPhone && shippingPhone; 
    });

    const showError = ref(false);
    const errorMessage = ref("");
    async function placeOrder() {
      if (!cart.value || !cart.value.id) {
        return;
      }

      try {
        const response = await make(cart.value.id) as any;

        if (response?.errors && response.errors.length > 0) {
          errorMessage.value = response.errors[0].message;
          showError.value = true;
          return;
        }

        if (response?.data?.placeOrder) {
          order.value = response.data.placeOrder;

          const orderNumber = order.value.order.order_number;

          showError.value = false;
          if (process.client) {
            sessionStorage.setItem('last_order_number', orderNumber);
          }

          console.log("Order created successfully:", orderNumber);
          await router.push({
            path: '/checkout/takk',
            query: { order: orderNumber }
          });
        }
      } catch (error) {
        console.error("Error placing order:", error.message);
        showError.value = true;
      }
    }

    const totalItems = computed(() => cartView.cartGetters.getTotalItems(cart.value));
    const subtotalExTax = computed(() => cart.value?.prices?.subtotal_excluding_tax?.value || 0);
    const subtotalIncludingTax = computed(() => cart.value?.prices?.subtotal_including_tax?.value || 0);
    const shippingTotal = ref(null);
    async function fetchShippingTotal() {
      if (!cart.value?.id) return;
      const cartAddress = cart.value.shipping_addresses?.[0];

      let country = props.sveaConfig?.default_country ?? 'NO';
      let postcode = props.sveaConfig?.default_postcode ?? '';


      if (cartAddress && cartAddress.country?.code && cartAddress.postcode) {
        country = cartAddress.country.code;
        postcode = cartAddress.postcode;
      }


      const address = {
        country_id: country,
        postcode
      };

      const shippingMethods = await getEstimatedShippingMethods(cart.value.id, address);
      if (shippingMethods.length > 0 && shippingMethods[0].amount) {
        shippingTotal.value = shippingMethods[0].amount;
      }
    }
    watchEffect(() => {
      if ((!shippingTotal.value || shippingTotal.value === 0) && cart.value?.id) {
        fetchShippingTotal();
      }
    });
    const totalsExclTax = computed(() => {
      const subtotal = subtotalExTax.value || 0;
      const shipping = shippingTotal.value || 0;
      return subtotal + shipping;
    });

    const totalsVat = computed(() => {
      return cart.value?.prices?.applied_taxes?.reduce((acc, tax) => acc + (tax.amount?.value || 0), 0) || 0;
    });
    const totals = computed(() => {
      return cart.value?.prices?.grand_total?.value || 0;
    });
    const discountValue = computed(() => {
      return cart.value?.prices?.discounts?.[0]?.amount?.value || 0;
    });

    const isCouponCodeApplied = computed(() => {
      return !!(cart.value?.prices?.discounts && cart.value.prices.discounts.length > 0);
    });
    return {
      totalItems,
      subtotalExTax,
      subtotalIncludingTax,
      getShippingAmount,
      shippingTotal,
      productTaxDiff,
      shippingTaxTotal,
      totalsVat,
      totalsExclTax,
      totals,
      canCheckout,
      hasValidPhones,
      isPlacingOrder,
      placeOrder,
      isSveaCheckout,
      creditCheckApproved,
      isAddressFormOpen,
      userTaxvat,
      showError,
      errorMessage,
      discountValue,
      isCouponCodeApplied,
      showCoupon
    };
  }
});
